import {PageTitle} from "../../components/Common/PageTitle/PageTitle";
import {Col, Flex, Modal, Row} from "antd";
import "./PracticesDetails.css"
import {LeftColumn} from "../../components/PracticesDetails/LeftColumn/LeftColumn";
import {RightColumn} from "../../components/PracticesDetails/RightColumn/RightColumn";
import {MiddleColumn} from "../../components/PracticesDetails/MiddleColumn/MiddleColumn";
import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {PracticesHttpService} from "../../services/Http/PracticesHttpService";
import {ClaimStatuses, PracticeDetail} from "../../models/Practice/PracticeDetail";
import {PracticeDiscounts} from "../../components/PracticesDetails/PracticeDiscounts/PracticeDiscounts";
import {EmptyLuxottica} from "../../components/Common/Empty/EmptyLuxottica";
import {SpinLuxottica} from "../../components/Common/SpinLuxottica/SpinLuxottica";
import {InformationService} from "../../services/InformationService";
import {Voucher, VoucherDetail} from "../../models/Info/voucher.model";
import {Utils} from "../../services/Utils";
import {AdminPracticeMenu} from "../../components/AdminPracticeMenu";
import {AuthService} from "../../services/AuthService";
import {NotificationService} from "../../services/NotificationService";
import {PracticeProgressStatus, PracticeType} from "../../models/Practice/Practice";
import {NoteList} from "../../components/PracticesDetails/NoteList";
import {ClaimStatusProvider} from "../../components/PracticesDetails/ClaimStatusProvider";
import {AttachmentType, PracticeFileRow} from "../../models/Practice/PracticeFIle";
import {TotalsInfo, buildPicQuotasNoteText} from "../../components/PracticesDetails/TotalsInfo";
import {ScriptDialogs} from "../../components/PracticesDetails/ScriptDialogs/ScriptDialogs";
import {InsuranceProviderIntegrationCodes} from "../../models/InsuranceProvider";
import {PracticeNotePutBody} from "../../models/Practice/PracticeNote";
import {InsuranceProviderAuthorizationHttpService} from "../../services/Http/InsuranceProviderAuthorizationHttpService";
import {InsuranceProviderAuthorizationOperationType} from "../../models/InsuranceProviderAuthorization";

export enum MiddleColumnStatus {
    INFO = "INFO",
    CHAT = "CHAT",
    NOTE = "NOTE",
}

/**
 * List of the insurance providers that PIC_AUTORIZZATA warning has been enabled.
 * N.B. This list doesn't include the AXA provider, because it needs a different handling.
 */
const PIC_AUTORIZZATA_ENABLED_LIST: string[] = [
    'GENERALI',
    'PREVIMEDICAL',
    'AON'
];

async function savePicQuotasNoteIfNotExists(id: string, insuranceProviderId: number, integrationCode: string, picAutomaticNoteIds: number[], insuranceAmount: number, patientAmount: number): Promise<void> {
    if (picAutomaticNoteIds.includes(insuranceProviderId)) {
        const noteText = buildPicQuotasNoteText(integrationCode, insuranceAmount, patientAmount);
        const existingNotesResponse = await PracticesHttpService.getPracticeNotes(id);
        const alreadyExists = existingNotesResponse.data.items.some(note => note.text === noteText);
        if (!alreadyExists) {
            const noteBody: PracticeNotePutBody = {
                operator: 'Manydesigns',
                text: noteText,
            };
            await PracticesHttpService.savePracticeNotes(noteBody, id);
        }
    }
}

export function PracticesDetails() {
    const location = useLocation()

    let {id} = useParams();
    const [detail, setDetails] = useState<PracticeDetail | undefined>()
    const [files,setFiles]= useState<Array<PracticeFileRow>>([])

    const [voucher,setVoucher] = useState<Voucher>()
    const [discounts,setDiscounts] = useState<Array<VoucherDetail>>([])

    const [middleColumnStatus, setMiddleColumnStatus] = useState<MiddleColumnStatus>(MiddleColumnStatus.INFO)
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigate = useNavigate();

    const [modal, contextHolder] = Modal.useModal();
    const [picAutomaticNoteIds, setPicAutomaticNoteIds] = useState<number[]>([]);

    useEffect(() => {
        InsuranceProviderAuthorizationHttpService.getIdsByOperationType(InsuranceProviderAuthorizationOperationType.PIC_AUTOMATIC_NOTE)
            .then(res => setPicAutomaticNoteIds(res.data))
            .catch(() => setPicAutomaticNoteIds([]));
    }, []);

    useEffect(() => {
        loadData(true);
    }, [location.key])

    const showPicAutorizzataPopUp = (details:PracticeDetail): boolean => {
        const integrationCode = details.insuranceProvider.integrationCode;
        const currentStatus = details.status;
        if (PracticeProgressStatus.PIC_AUTORIZZATA === currentStatus && PIC_AUTORIZZATA_ENABLED_LIST.includes(integrationCode)) {
            return true;
        }
        const isLastDocDaInviare = Utils.getLastProgressData(details.progress)?.name === PracticeProgressStatus.DOCUMENTAZIONE_DA_INVIARE;
        return integrationCode === InsuranceProviderIntegrationCodes.AXA && isLastDocDaInviare;
    }

    const loadData = async (showNoteWarning: boolean = false) => {
        setIsLoading(true);
        try {
            const response = await PracticesHttpService.getPracticeDetail(id);
            const detail = response.data;
            setDetails(detail)
            const loadedFiles = await loadFiles();
            if (detail.status === PracticeProgressStatus.ERRORE_IN_FATTURA && id) {
                try {
                    const totals = await PracticesHttpService.getClaimTotalQuotas(id);
                    await savePicQuotasNoteIfNotExists(id, detail.insuranceProvider.id, detail.insuranceProvider.integrationCode, picAutomaticNoteIds, totals.data.insuranceAmount, totals.data.patientAmount);
                } catch (_) {}
            }
            if (showNoteWarning) {
                if ([PracticeProgressStatus.ERRORE_IN_DOCUMENTAZIONE, PracticeProgressStatus.ERRORE_IN_FATTURA, PracticeProgressStatus.PIC_NON_AUTORIZZATA].includes(detail.status)) {
                    const response = await PracticesHttpService.getPracticeNotes(id);
                    const isPicNonAutorizzata = detail.status === PracticeProgressStatus.PIC_NON_AUTORIZZATA;
                    await modal.warning({
                        title: isPicNonAutorizzata ? 'Pratica in pic non autorizzata!' : 'Pratica in rettifica!',
                        width: 500,
                        content: <>
                            Controlla le note qui sotto per avere ulteriori informazioni!
                            <NoteList data={response.data.items}/>
                        </>
                    })
                } else if (showPicAutorizzataPopUp(detail)) {
                    try {
                        const response = await PracticesHttpService.getClaimTotalQuotas(id);
                        modal.warning({
                            title: 'Pratica con pic autorizzata!',
                            width: 500,
                            content: <TotalsInfo detail={detail} loadedFiles={loadedFiles} refundType={detail?.agreement} integrationCode={detail.insuranceProvider.integrationCode} data={response.data} />
                        });
                        // Save PIC quotas as a note so totals remain visible in ELRI after the popup is dismissed.
                        if (id) {
                            const { insuranceAmount, patientAmount } = response.data;
                            await savePicQuotasNoteIfNotExists(id, detail.insuranceProvider.id, detail.insuranceProvider.integrationCode, picAutomaticNoteIds, insuranceAmount, patientAmount);
                        }
                    } catch (error:any) {
                        console.error("Errore in risposta totale claim ", error.message);
                    }
                } else {
                    const estimateLoaded = loadedFiles.find(file => file.type == AttachmentType.ESTIMATE) != undefined;
                    if (!estimateLoaded && detail.insuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.AXA && detail.agreement === PracticeType.DIRECT) {
                        modal.warning({
                            title: 'Attenzione!',
                            width: 500,
                            content: <>
                                <Row gutter={16}>Caricare il preventivo e ricaricare la pagina del Portale Unico per avere l’importo esatto delle quote da allocare in fattura</Row>
                                {detail.picNotes &&
                                    <Row gutter={16} style={{marginTop: "16px"}}>
                                        <strong>{detail.picNotes}</strong>
                                    </Row>
                                }
                            </>
                        })
                    }
                }
            }
            const {data: voucherData} = await InformationService.getVoucherDetails(detail.shopGroup, detail.insuranceProvider.id, detail.agreement)
            setVoucher(voucherData);
            let discountsWithKeys: VoucherDetail[] = Utils.addReactUniqueKey(voucherData.discounts);
            setDiscounts(discountsWithKeys);
        } catch (e) {
            console.log(e);
            NotificationService.getInstance().openNotificationError("Impossibile caricare il dettaglio della pratica")
        } finally {
            setIsLoading(false);
        }
    };

    const loadFiles = async () => {
        const response = await PracticesHttpService.getPracticeFiles(id)
        const filesWithKey:PracticeFileRow[] = Utils.addReactUniqueKey(response.data.items);
        setFiles(filesWithKey)
        return filesWithKey;
    };

    function onClickChatButton() {
        setMiddleColumnStatus(MiddleColumnStatus.CHAT);
    }

    function onClickTransformToIndirect() {
        if (id == undefined) return;

        PracticesHttpService.transformToIndirect(id).then((response)=>{
            const newClaimId = response.data;
            navigate(`/practices/details/${newClaimId}`, {replace: true})
        })
    }

    function onClickNoteButton() {
        setMiddleColumnStatus(MiddleColumnStatus.NOTE);
    }
    function goToInfo(){
        setMiddleColumnStatus(MiddleColumnStatus.INFO)
    }

    function showScriptsDialog(detail:PracticeDetail):boolean{
        return (detail.insuranceProvider.code === 'PREV') || (detail.agreement == PracticeType.INDIRECT);
    }

    return <>
        <SpinLuxottica loading={isLoading}>
            {contextHolder}
            <ClaimStatusProvider claimStatus={detail? detail.claimStatus : ""} >
            <div className={"practices-details"}>
                <Flex align={"center"} justify={"space-between"}>
                    <PageTitle title={"Pratiche"} historyBack={true} ></PageTitle>
                    <Flex gap={5}>
                        {
                            AuthService.isAdmin() && <AdminPracticeMenu reloadData={loadData} />
                        }
                        {
                            (detail && voucher) &&
                                <PracticeDiscounts voucher={voucher} discounts={discounts}
                                                   insuranceProviderId={detail?.insuranceProvider?.id}></PracticeDiscounts>
                        }
                        {
                            (detail && voucher && showScriptsDialog(detail)) &&
                            <ScriptDialogs voucher={voucher} detail={detail}
                                               insuranceProviderId={detail?.insuranceProvider?.id}></ScriptDialogs>
                        }
                    </Flex>
                </Flex>
                {
                    detail ?
                        <Row gutter={[16, 8]} className={"row-container"}>
                            <Col span={5}>
                                <div className="col-container">
                                    <LeftColumn agreement={detail.agreement} client={detail?.patient.name ?? ""} email={detail?.patient.email ?? ""}
                                                fiscalCode={detail?.patient.cf ?? ""}
                                                phoneNumber={detail?.patient.phone}
                                                code={detail?.code}
                                                onClickTransformToIndirect={onClickTransformToIndirect}
                                                onClickNoteButton={onClickNoteButton}
                                                status={detail?.status}
                                    ></LeftColumn>
                                </div>
                            </Col>
                            <Col span={14}>
                                <div className="col-container">
                                    <MiddleColumn status={middleColumnStatus}
                                                  practiceDetail={detail}
                                                  attachedFiles={files}
                                                  onSaveFile={loadFiles}
                                                  changeVisus={voucher?.visus}
                                                  onClickSavePurchase={loadData}
                                                  onClickConfirmDocs={loadData}
                                                  onClickExit={goToInfo}
                                                  onSaveMessage={loadData}
                                                  reloadDetail={loadData}
                                                  handleMiddleColumnSpin={(value:boolean) => setIsLoading(value)}
                                    />
                                </div>
                            </Col>
                            <Col span={5}>
                                <div className="col-container">
                                    <RightColumn claimStatus={detail?.claimStatus || ClaimStatuses.OPEN} operator={detail?.operator ?? ""} date={detail?.voucherDate ?? ""}
                                                 progress={detail?.progress}
                                                 status={detail?.status}
                                                 manage={detail?.agreement}
                                                 onClickChatButton={onClickChatButton}
                                                 onCloseModal={loadData}></RightColumn>
                                </div>
                            </Col>
                        </Row>
                        : <EmptyLuxottica></EmptyLuxottica>
                }
            </div>
            </ClaimStatusProvider>
        </SpinLuxottica>


    </>
}
